import React, { useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { ArrowRightIcon, ChevronLeftIcon, Loader2Icon } from 'lucide-react';
import { pages, getPageBySlug } from '../data/pages';
import { FormEmbed } from '../components/FormEmbed';
const FORM_ORIGIN = 'https://letlois-onboarding-forms.josh-40d.workers.dev';
const FORM_SOURCE = 'letlois-onboarding';
const LEAVE_WARNING = 'Your files are still uploading. If you leave now, the upload may not finish. Leave anyway?';
interface FormMessage {
  source?: string;
  type?: string;
}
export function FormScreen() {
  const {
    slug = ''
  } = useParams();
  const page = getPageBySlug(slug);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    window.scrollTo({
      top: 0
    });
    setBusy(false);
  }, [slug]);

  // Listen for upload status from the embedded form.
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== FORM_ORIGIN) return;
      const data = event.data as FormMessage | null;
      if (!data || typeof data !== 'object' || data.source !== FORM_SOURCE) return;
      if (data.type === 'busy') setBusy(true);else if (data.type === 'done' || data.type === 'error') setBusy(false);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  // Warn before closing or reloading the tab mid-upload.
  useEffect(() => {
    if (!busy) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [busy]);
  if (!page || !page.formEmbedUrl) return <Navigate to={`/${pages[0].slug}`} replace data-id="element-66" data-mp="1qzxcuc7bjpt2" />;
  const index = pages.findIndex(p => p.id === page.id);
  const next = pages[index + 1];
  const confirmLeave = (e: React.MouseEvent) => {
    if (busy && !window.confirm(LEAVE_WARNING)) e.preventDefault();
  };
  return <div className="w-full pb-14 pt-3 sm:pb-20 sm:pt-6" data-id="element-67" data-mp="y47n38g5phx5">
      <div className="mx-auto flex w-full max-w-4xl items-center justify-between gap-4 px-4 sm:px-8" data-id="element-68" data-mp="t4l9gxwf1znt">
        <Link to={`/${page.slug}`} onClick={confirmLeave} className="inline-flex items-center gap-1 rounded text-[12px] font-medium text-white/25 transition-colors duration-150 ease-smooth hover:text-white/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-300" data-id="element-69" data-mp="p285adxbi9t5">
          <ChevronLeftIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-70" data-mp="1ctdkqrzcj02x" />
          Back to the video
        </Link>
        <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-brand-200 sm:text-xs" data-id="element-71" data-mp="180ay73szkqw8">
          {page.eyebrow} form
        </p>
      </div>

      <h1 className="mx-auto mt-3 max-w-4xl px-4 text-xl font-extrabold leading-tight tracking-tight text-white sm:mt-5 sm:px-8 sm:text-3xl" data-id="element-72" data-mp="20hn1hfhpcxn7">
        {page.title}
      </h1>

      <div className="mt-4 sm:mt-6" data-id="element-73" data-mp="1vp3p2lur48jz">
        <FormEmbed src={page.formEmbedUrl} title={`${page.eyebrow} onboarding form`} data-id="element-74" data-mp="232uq8jd4woyo" />
      </div>

      {next && <div className="mx-auto mt-6 flex w-full max-w-4xl flex-col items-center gap-2 px-4 sm:mt-8 sm:px-8" data-id="element-75" data-mp="281o8sq7a365p">
          {busy ? <button type="button" disabled aria-live="polite" className="inline-flex w-full cursor-not-allowed items-center justify-center gap-2 rounded-full bg-white/[0.08] px-6 py-3.5 text-base font-semibold text-white/60 sm:w-auto sm:px-8" data-id="element-76" data-mp="2h2495qsq5x6c">
              <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-77" data-mp="2vqgvrx5xa3o6" />
              Still uploading — keep this page open
            </button> : <Link to={`/${next.slug}`} className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand-500 px-6 py-3.5 text-base font-semibold text-white transition-colors duration-150 ease-smooth hover:bg-brand-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-300 focus-visible:ring-offset-2 focus-visible:ring-offset-ink-900 sm:w-auto sm:px-8" data-id="element-78" data-mp="30rwhlrfxce7m">
              I&apos;ve submitted this — continue
              <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-79" data-mp="35t2ofxny20ee" />
            </Link>}
          <p className="text-[12px] text-white/35" data-id="element-80" data-mp="30vjayzz2lrni">
            Only continue once you&apos;ve hit submit on the form above.
          </p>
        </div>}
    </div>;
}